import { creatorbase } from '@round/api';
import { FormikProvider, useFormik } from 'formik';
import { FormValues, validationSchema } from '../components/PurchaseOrderForm/helpers';
import { useCurrencies } from 'contexts/OptionsContext/useCurrencies';
import { mapBillingEntityToOption } from '../../../BillingEntities/hooks/useBillingEntitySelect';
import { getObjectDifference, showNotification } from 'helpers';
import { useMemo } from 'react';
import { Modal } from '@round/whitelabel-ui';
import { Button } from '@round/whitelabel-ui';
import styles from './UpdatePurchaseOrderModal.module.css';
import { mapApiErrorsToFormikErrors } from 'utility/utility';
import {
    useBillingEntity,
    useInvalidatePurchaseOrders,
    useTeam,
    useUpdatePurchaseOrder,
} from '@round/creatorbase-helpers/api-hooks';
import { mapTeamToOption } from 'Modules/Plans/Team/hooks/useTeamSelect';
import { skipToken, useQuery } from '@tanstack/react-query';
import { file, network } from '@round/utils';
import PurchaseOrderForm from '../components/PurchaseOrderForm/PurchaseOrderForm';

const mapToApi = (values: FormValues): creatorbase.PatchPurchaseOrderData => ({
    number: values.isInternal ? undefined : values.poNumber,
    request_reference: values.isInternal ? undefined : values.request_reference,
    amount: values.amount,
    description: values.description,
    currency_id: values.currency?.value!,
    billing_entity_id: values.billingEntity?.value!,
    file_upload: values.fileUpload ?? undefined,
    team_id: values.team?.value!,
    is_internal: values.isInternal,
});

type Props = {
    isOpen: boolean;
    onClose: () => void;
    purchaseOrder: creatorbase.PurchaseOrder | null;
};

const UpdatePurchaseOrderModal = ({ isOpen, onClose, purchaseOrder }: Props) => {
    const { options } = useCurrencies();
    const { data: billingEntity } = useBillingEntity(purchaseOrder?.billing_entity_id);
    const { data: team } = useTeam(purchaseOrder?.team_id);
    const {
        update,
        helpers: { isExpectedErrorResponse },
    } = useUpdatePurchaseOrder(purchaseOrder?.id);
    const { invalidatePurchaseOrders } = useInvalidatePurchaseOrders();

    const fileQuery = useQuery({
        queryKey: ['file', purchaseOrder?.file_upload],
        queryFn: !purchaseOrder?.file_upload
            ? skipToken
            : async () => {
                  const response = await network.fetchFile(purchaseOrder?.file_upload!);
                  if (response instanceof File) {
                      return response;
                  }

                  throw new Error('Could not fetch file');
              },
        enabled: !!purchaseOrder?.file_upload,
    });

    const initialValues = useMemo(
        () => ({
            poNumber: purchaseOrder?.number ?? '',
            amount: purchaseOrder?.amount ?? 0,
            currency: options.find((c) => c.value === purchaseOrder?.currency_id) ?? null,
            fileUpload: fileQuery.data || null,
            description: purchaseOrder?.description ?? '',
            billingEntity: billingEntity ? mapBillingEntityToOption(billingEntity) : null,
            request_reference: purchaseOrder?.request_reference ?? '',
            team: team ? mapTeamToOption(team) : null,
            isInternal: purchaseOrder?.is_internal ?? false,
        }),
        [
            fileQuery.data,
            billingEntity,
            options,
            purchaseOrder?.amount,
            purchaseOrder?.currency_id,
            purchaseOrder?.description,
            purchaseOrder?.number,
            purchaseOrder?.request_reference,
            purchaseOrder?.is_internal,
            team,
        ]
    );

    const formik = useFormik<FormValues>({
        initialValues,
        enableReinitialize: true,
        validationSchema: validationSchema,
        onSubmit: (values, helpers) => {
            helpers.setSubmitting(true);
            const diff = getObjectDifference(mapToApi(values), mapToApi(initialValues));
            update(diff, {
                onSuccess: () => {
                    showNotification('PO updated', 'info');
                    helpers.resetForm();
                    invalidatePurchaseOrders(purchaseOrder?.id);
                    onClose();
                },
                onError: (error) => {
                    if (!isExpectedErrorResponse(error)) {
                        showNotification('Could not update PO', 'error');
                        return;
                    }

                    const response = error.response;
                    if (response.status === 403 || response.status === 404) {
                        showNotification(response.data.detail, 'error');
                        return;
                    }

                    helpers.setErrors(
                        mapApiErrorsToFormikErrors({
                            fileUpload: response.data.file_upload,
                            billingEntity: response.data.billing_entity_id,
                            currency: response.data.currency_id,
                            description: response.data.description,
                            request_reference: response.data.request_reference,
                            amount: response.data.amount,
                            poNumber: response.data.number,
                            team: response.data.team_id,
                        })
                    );
                },
                onSettled: () => {
                    helpers.setSubmitting(false);
                },
            });
        },
    });

    const handleClose = () => {
        onClose();
        formik.resetForm();
    };

    return (
        <Modal className={styles.modal} closeOnEscape closeOnOverlayClick isOpen={isOpen} onClose={handleClose}>
            <Modal.Header>
                <Modal.Title>Update Purchase Order</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <FormikProvider value={formik}>
                    <PurchaseOrderForm />
                </FormikProvider>
            </Modal.Body>

            <Modal.Actions>
                <Button
                    appearance="primary"
                    type="button"
                    onClick={formik.submitForm}
                    isLoading={formik.isSubmitting}
                    disabled={
                        formik.isSubmitting ||
                        (!formik.dirty &&
                            !file.getHasFileChanged(formik.values.fileUpload, formik.initialValues.fileUpload))
                    }
                >
                    Update PO
                </Button>

                <Button appearance="ghost" type="button" onClick={handleClose}>
                    Cancel
                </Button>
            </Modal.Actions>
        </Modal>
    );
};

export default UpdatePurchaseOrderModal;
